import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, Typography } from '@mui/material';
import { Download, TextSnippet } from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import ListSection from 'components/_layouts/ListSection';
import useIssuedManual from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { Policy } from 'views/App/Portal/Policy/_types/policy';
import { IssuedManualsContext } from '../context';
import { ActionMenu } from 'components/_atoms';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import ExportModal from '../_components/ExportModal';

const columnHelper = createColumnHelper<Policy>();

const IssuedManualsChapter: React.FC = () => {
  const { id, chapterId } = useParams();
  const { manualHasPermissions, handleDownload } = useContext(IssuedManualsContext);
  const manualQuery = useIssuedManual(id || '');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  const handleExportClick = (policy: Policy) => {
    setSelectedPolicy(policy);
    setExportModalOpen(true);
  };

  const chapter = useMemo(() => {
    if (manualQuery.data && manualQuery.data.chapters) {
      return manualQuery.data.chapters.find((x) => x.id === chapterId);
    }
  }, [chapterId, manualQuery.data]);

  const renderActions = (item: any) => {
    return [
      ...(manualHasPermissions(id ?? '', ['createManualAcknowledgement'])
        ? [
            {
              key: `new_${item.id}`,
              icon: <TextSnippet />,
              label: t('policy.issuedManuals.actions.newAdhocAcknowledgement'),
              href: `../${id}/policy/${item.id}/new-acknowledgement`,
            },
          ]
        : []),
      {
        key: `download_${item.id}`,
        icon: <Download sx={{ marginTop: 1 }} />,
        label: t('policy.issuedManuals.actions.pdf'),
        onClick: () => handleExportClick(item),
      },
    ];
  };

  const columns = [
    columnHelper.accessor('title', {
      cell: (info) => (
        <Link href={`../${id}/policy/${info.row.original.id}`}>
          {[info.row.original.key, info.row.original.title].join(': ')}
        </Link>
      ),
      size: 9999,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) =>
        renderActions(info.row.original).length > 0 ? (
          <ActionMenu
            id={`action_menu_${info.row.original.id}`}
            items={renderActions(info.row.original)}
          />
        ) : null,
      header: () => '',
      size: 60,
    }),
  ];

  return (
    <TakeoverLayout
      backUrl={`../${id}`}
      pageTitle={chapter ? [chapter.key, chapter.title].join(': ') : ''}
      headerProps={{
        title: chapter ? [chapter.key, chapter.title].join(': ') : '',
        breadcrumbItems: [
          <Link
            key='b1'
            href='..'
          >
            {t('policy.issuedManuals.title')}
          </Link>,
          <Link
            key='b2'
            href={`../${id}`}
          >
            {manualQuery.data?.name}
          </Link>,
          <Typography key='b3'>{chapter ? [chapter.key, chapter.title].join(': ') : ''}</Typography>,
        ],
      }}
    >
      <ListSection
        columns={columns}
        data={chapter?.policies || []}
        hideHeaders
        query={manualQuery}
        showChrome={true}
      />
      {selectedPolicy && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => {
            setExportModalOpen(false);
            setSelectedPolicy(null);
          }}
          exportType='content'
          manualId={id!}
          contentId={selectedPolicy.id}
          contentType='POLICY'
          contentTitle={selectedPolicy.title}
        />
      )}
    </TakeoverLayout>
  );
};

export default IssuedManualsChapter;
